import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { differenceBy, find, isEmpty } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { map, share } from 'rxjs/operators';

import { Field, FieldKinds, TableCellKinds, Template, User } from '@site-mate/dashpivot-shared-library';

import { AnalyticsService } from 'app/analytics/analytics.service';
import { FormService } from 'app/form/form.service';
import { MultiSelectFilterOption } from 'app/form-fields/common/register/filters/multi-select/multi-select-filter-option';
import { ChartFilter } from 'app/shared/model/charts/chart-filter.model';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartTypes } from 'app/shared/model/charts/chart-types.enum';

@Component({
  selector: 'cc-chart-view-modal-filters',
  templateUrl: './chart-view-modal-filters.component.html',
  styleUrls: ['./chart-view-modal-filters.component.scss'],
})
export class ChartViewModalFiltersComponent implements OnChanges {
  @Input() chart: ChartSettings;
  @Output() onChange = new EventEmitter();

  filters: ChartFilter[] = [];

  visibleFilters: ChartFilter[] = [];
  availableFilters: ChartFilter[] = [];
  newFilterIsVisible: boolean;
  emptyFilters = new Map<string, boolean>();

  constructor(
    private readonly formService: FormService,
    private readonly analyticsService: AnalyticsService,
  ) {}

  ngOnChanges(): void {
    this.chart.filters = this.chart.filters || {};
    this.updateFilters();
  }

  showNewFilter() {
    this.newFilterIsVisible = true;
  }

  hideNewFilter() {
    this.newFilterIsVisible = false;
  }

  onFilterTypeChange(oldFilter: ChartFilter) {
    this.availableFilters = differenceBy(this.filters, this.visibleFilters, 'id');
    this.onFilterChange(oldFilter);
  }

  onAddFilter(filter: ChartFilter) {
    this.visibleFilters.push(filter);
    this.availableFilters = differenceBy(this.filters, this.visibleFilters, 'id');
    this.newFilterIsVisible = false;
    this.updateEmptyFilters(filter.id);
  }

  onRemoveFilter(index) {
    const [filter] = this.visibleFilters.splice(index, 1);
    this.availableFilters = differenceBy(this.filters, this.visibleFilters, 'id');
    this.onFilterChange(filter);
  }

  updateEmptyFilters(id, value = null) {
    this.emptyFilters.delete(id);
    if (!value && find(this.visibleFilters, { id })) {
      this.emptyFilters.set(id, true);
    }
  }

  onFilterChange(filter: ChartFilter, params = null) {
    this.updateEmptyFilters(filter.id, params);

    const [fieldId, columnId] = filter.id.split('.');
    if (!fieldId && !columnId) {
      return;
    }

    let chartFilter = this.chart.filters[fieldId] || {};

    if (fieldId && !columnId) {
      chartFilter = params;
    }

    if (fieldId && columnId) {
      delete chartFilter[columnId];
      if (params) {
        chartFilter[columnId] = params;
      }
    }

    delete this.chart.filters[fieldId];

    if (chartFilter && !isEmpty(chartFilter)) {
      this.chart.filters[fieldId] = chartFilter;
    }

    this.onChange.emit(this.chart);
  }

  private updateFilters() {
    this.filters = [];

    if (this.chart.template.isOrganisationTemplate) {
      this.filters.push(...this.getProjectAndTeamFilters(this.chart.template));
    }

    this.filters.push(this.getUserFilter(this.chart.template));

    if (this.chart.template.columns && this.chart.template.columns.length) {
      this.filters.push(this.getWorkflowColumnsFilter(this.chart.template));
    }

    this.filters.push(...this.getTemplateFilters(this.chart.template, this.chart));

    // remove no longer existing filters from visible filters
    this.visibleFilters = this.visibleFilters.filter((visibleFilter) =>
      this.filters.some((filter) => visibleFilter.id === filter.id),
    );

    // add new visible filters
    this.visibleFilters.push(...this.filters.filter((filter) => this.shouldAddToVisibleFilters(filter)));

    this.availableFilters = differenceBy(this.filters, this.visibleFilters, 'id');
  }

  private getTemplateFilters(template, chart) {
    const filters = [];
    const filterTypes = [FieldKinds.Category];

    // TODO: add prefilled table list cell filter
    if (chart.kind === ChartTypes.Quantity) {
      filterTypes.push(FieldKinds.Table);
    }

    template.items
      .filter((item) => filterTypes.includes(item.kind))
      .forEach((item) => {
        if (item.kind === FieldKinds.Category) {
          filters.push(this.getCategoryFilter(template, item));
          return;
        }

        if (
          item.kind === FieldKinds.Table &&
          item._id === chart.itemId &&
          chart.kind === ChartTypes.Quantity
        ) {
          filters.push(...this.getTableFilters(template, item));
        }
      });

    return filters;
  }

  private shouldAddToVisibleFilters(filter: ChartFilter) {
    const filterIds = filter.id.split('.');
    let filterExists = filterIds[0] in this.chart.filters;

    if (filterExists && filterIds.length > 1) {
      filterExists = filterExists && filterIds[1] in this.chart.filters[filterIds[0]];
    }

    const isVisible = this.visibleFilters.some((visibleFilter) => visibleFilter.id === filter.id);

    return filterExists && !isVisible;
  }

  private getUserFilter(template: Template): ChartFilter {
    const item = { _id: 'createdBy', kind: 'user', description: 'Created By' };

    const filter = {
      id: item._id,
      item,
      name: item.description,
      state: this.chart.filters[item._id],
      options: this.analyticsService.getFormUsers(template._id).pipe(
        map((users: User[]) => {
          return users.map((user) => ({ label: user.fullName, value: user._id }));
        }),
      ),
    };

    return filter;
  }

  private getWorkflowColumnsFilter(template: Template): ChartFilter {
    const item = { _id: 'workflowStep', kind: 'category', description: 'Workflow step' };
    const options = of(template.columns.map((column, index) => ({ label: column.title, value: index + 1 })));
    const filter = {
      id: item._id,
      item,
      name: item.description,
      state: this.chart.filters[item._id],
      options,
    };

    return filter;
  }

  private getProjectAndTeamFilters(template: Template): ChartFilter[] {
    const [projects$, teams$] = this.getProjectsAndTeams(template._id);

    const projectItem = { _id: 'project', kind: 'category', description: 'Project' };
    const projectFilter = {
      id: projectItem._id,
      item: projectItem,
      name: projectItem.description,
      state: this.chart.filters[projectItem._id],
      options: projects$,
    };

    const teamItem = { _id: 'team', kind: 'category', description: 'Team' };
    const teamFilter = {
      id: teamItem._id,
      item: teamItem,
      name: teamItem.description,
      state: this.chart.filters[teamItem._id],
      options: teams$,
    };

    return [projectFilter, teamFilter];
  }

  private getProjectsAndTeams(templateId: string): Observable<MultiSelectFilterOption[]>[] {
    const orig$ = this.formService.getFormProjects(templateId).pipe(share());

    const projects$ = orig$.pipe(
      map((projects: any[]) => {
        return projects.map((project) => ({ label: project.name, value: project._id }));
      }),
    );

    const teams$ = orig$.pipe(
      map((projects: any[]) => {
        const teams = [];
        projects.forEach((project) => {
          teams.push(
            ...project.teams.map((team) => {
              return { label: team.name, value: team._id };
            }),
          );
        });

        return teams;
      }),
    );

    return [projects$, teams$];
  }

  private getCategoryFilter(template: Template, field: Field): ChartFilter {
    const filter = {
      id: field._id,
      item: field,
      name: field.description,
      state: this.chart.filters[field._id],
      options: this.formService
        .getFormItemValues(template._id, field._id)
        .pipe(map((values: string[]) => values.map((value) => ({ value, label: value })))),
    };

    return filter;
  }

  private getTableFilters(template: Template, table: Field): ChartFilter[] {
    const tableState = this.chart.filters[table._id] || {};
    return table.columns
      .filter((column) => column.kind === TableCellKinds.List)
      .map((column) => {
        const filter = {
          id: [table._id, column._id].join('.'),
          item: column,
          name: column.heading,
          state: tableState[column._id],
          options: this.analyticsService
            .getFormItemTableColumnValues(template._id, table._id, column._id)
            .pipe(map((values: string[]) => values.map((value) => ({ value, label: value })))),
        };

        return filter;
      });
  }
}
